/**
 * Event landing-page template — soiree ("Abend bei Kerzenlicht").
 *
 * P9-W5: THIRD premium 2026 template. Distinct CraftJS home-page document for
 * the `soiree` theme — midnight-noir poster register for galas, dinners and
 * award nights: near-black plum stage, champagne-gold accent, Playfair Display
 * display serif over Hanken Grotesk body, candlelight glow discs on the hero
 * and an evening timeline from aperitif to last dance.
 *
 * The candlelight art + motion live in `soireePreset.customCss`
 * (self-hosted @font-face, @keyframes glow-float/fade-up, scroll-reveal via
 * `animation-timeline: view()`, everything motion-gated behind
 * `prefers-reduced-motion: no-preference`). Section DOM ids = node ids, so
 * the css targets `#sre_*` directly.
 *
 * Convention (mirrors networkingNight/gala siblings):
 *  - Section-level marketing copy goes through td() keys (`sre*` prefix).
 *    Hero copy keys end in `HeroHeader` / `HeroEyebrow` (P9-W2 fact hooks).
 *  - Repetitive structured child data (agenda rows, numbers, FAQ) also goes
 *    through td() keys (`sreNum*` / `sreAgendaRow*` / `sreFaq*`) — Wave 3b
 *    moved the former inline German literals into `websiteBuilderDefaults`.
 *  - Hero primary CTA anchors "#"+DataInput node id, so OPEN registration's
 *    strip + dead-anchor fixup applies cleanly.
 */
import { defaultRegistrationConsents, toCheckboxItems } from "@/lib/website/registrationConsents";
import {
  A,
  enc,
  pv,
  tc,
  btn,
  fi,
  acceptanceFi,
  scaffold,
  emptyFieldsGroup,
  PAD,
  DIST_CARD,
  BTN_FORM_SP,
  SEC_MARGIN,
  ACCEPTANCE_VISIBILITY_FILTER,
  type N,
  type TranslationFn,
} from "../temp";
import { EASE, decorLayer, fadeUpKeyframes, fontFaces, heroShell, motionGate, revealBlock } from "./premium-css";
import type { WebsiteThemeStylePreset } from "@/types/WebsiteTheme";

// Reuse the seeded 03-gala asset namespace for the (hidden-by-default)
// Gallery — soiree ships no photo library of its own; the hero is pure CSS
// candlelight art. These are the exact keys gala.ts already references.
const W = `${A}/shared/templates/03-gala`;

// Midnight-noir palette (candlelight: plum ink + champagne gold)
const PLUM = "#171022"; // near-black plum stage (sections)
const PLUM_DEEP = "#100A18"; // sunk plum (page base, footer)
const SURFACE = "#1E1530"; // raised plum surface (alternating sections)
const GOLD = "#D9B25F"; // champagne-gold accent + primary CTA
const GOLD_SOFT = "#F3E3BC"; // pale champagne (headlines on dark)
const ON_STAGE = "#EFE9F4"; // body text on plum
const MUTE = "#A99CB8"; // muted body on plum
const LINE = "#2C2138"; // hairlines / countdown borders on dark
const WHITE = "#FFFFFF";
const DISPLAY = "'Playfair Display', Didot, Georgia, serif";
const BODY_FONT = "'Hanken Grotesk', 'Helvetica Neue', sans-serif";

const LOGO = `${A}/shared/logo.svg`;
const SRE_IMG = [
  `${W}/detail-champagne.jpg`, `${W}/detail-gold-cutlery.jpg`, `${W}/detail-napkin.jpg`,
  `${W}/detail-place-card.jpg`, `${W}/hero-zusage.jpg`, `${W}/hero-save-the-date.jpg`,
];

// ── Structured child data (evening register, td-keyed since Wave 3b) ──

const numbersData = (td: TranslationFn) => [
  { num: "<b>5</b>", desc: td("sreNum1Desc") },
  { num: "<b>12</b>", desc: td("sreNum2Desc") },
  { num: `<b>${td("sreNum3Label")}</b>`, desc: td("sreNum3Desc") },
  { num: "<b>01:00</b>", desc: td("sreNum4Desc") },
];

const agendaData = (td: TranslationFn) =>
  ([1, 2, 3, 4, 5] as const).map((n) => ({
    time: `<b>${td(`sreAgendaRow${n}Time`)}</b>`,
    title: td(`sreAgendaRow${n}Title`),
    who: td(`sreAgendaRow${n}Who`),
    desc: td(`sreAgendaRow${n}Desc`),
  }));

const faqData = (td: TranslationFn) =>
  ([1, 2, 3, 4, 5] as const).map((n) => ({
    q: `<b>${td(`sreFaq${n}Question`)}</b>`,
    a: td(`sreFaq${n}Answer`),
  }));

// ── soiree-specific child factories ───────────────────────────────────

function numbersChild(parent: string, i: number, td: TranslationFn): N {
  const d = numbersData(td)[i];
  return {
    type: { resolvedName: "NumbersChild" }, isCanvas: false,
    props: {
      isVisible: true,
      isVisibleNumber: true, textNumber: d.num, fontSizeNumber: "text-4xl", textAlignNumber: "center",
      isVisibleDescription: true, textDescription: d.desc, fontSizeDescription: "text-sm", textAlignDescription: "center",
    },
    displayName: "NumbersChild", custom: { icon: "IconNumber" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function agendaChild(parent: string, i: number, td: TranslationFn): N {
  const d = agendaData(td)[i];
  return {
    type: { resolvedName: "AgendaChild" }, isCanvas: false,
    props: {
      isVisibleText1: true, textText1: d.time, fontSizeText1: "text-sm", textAlignText1: "left",
      isVisibleText2: true, textText2: d.title, fontSizeText2: "text-sm", textAlignText2: "left",
      isVisibleText3: true, textText3: d.who, fontSizeText3: "text-sm", textAlignText3: "left",
      isVisibleText: true, textText: d.desc, fontSizeText: "text-sm", textAlignText: "left", border: true,
    },
    displayName: "AgendaChild", custom: { icon: "IconCalendar" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function faqChild(parent: string, i: number, td: TranslationFn): N {
  const d = faqData(td)[i];
  return {
    type: { resolvedName: "FaQChild" }, isCanvas: false,
    props: {
      isVisibleQuestion: true, textQuestion: d.q, fontSizeQuestion: "text-sm", textAlignQuestion: "left",
      isVisibleAnswer: true, textAnswer: d.a, fontSizeAnswer: "text-sm", textAlignAnswer: "left", border: true,
    },
    displayName: "FaQChild", custom: { icon: "IconMessageQuestion" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function galleryImage(parent: string, i: number): N {
  return {
    type: { resolvedName: "ImageChild" }, isCanvas: false,
    props: { src: SRE_IMG[i % SRE_IMG.length], isVisible: true, aspectRatio: "aspect-video", displayName: "ImageChild", width: "w-full", canDuplicate: true },
    displayName: "ImageChild", custom: { icon: "IconSection" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

// ── Theme CSS (P9-W5) ─────────────────────────────────────────────────
//
// Marker comment first so a later user edit is distinguishable from the
// pristine preset. Composed from the shared premium-css builders (WS3) —
// output pinned byte-for-byte in __tests__/premiumCss.test.ts. The static
// plum stage + candle glow discs remain without motion.
const CUSTOM_CSS = `/* theme:soiree */
${fontFaces([
  { family: "Playfair Display", file: "playfair-display", weightRange: "400 900" },
  { family: "Hanken Grotesk", file: "hanken-grotesk" },
])}

/* Poster hero — midnight plum stage, faint champagne washes in the corners */
#sre_hero {
${heroShell}
  background-image:
    radial-gradient(circle 280px at 88% 4%, rgba(217, 178, 95, 0.20) 0%, rgba(217, 178, 95, 0) 70%),
    radial-gradient(circle 220px at 2% 96%, rgba(217, 178, 95, 0.14) 0%, rgba(217, 178, 95, 0) 70%),
    linear-gradient(165deg, #231735 0%, ${PLUM} 55%, ${PLUM_DEEP} 100%);
}

/* Candlelight glow discs (the networkingNight dot recipe, champagne palette) */
#sre_hero::before,
#sre_hero::after {
${decorLayer}
  border-radius: 50%;
  filter: blur(6px);
}
#sre_hero::before {
  width: 200px;
  height: 200px;
  top: 9%;
  right: 7%;
  background:
    radial-gradient(circle at 50% 42%, rgba(243, 227, 188, 0.5) 0%, rgba(217, 178, 95, 0.2) 52%, rgba(217, 178, 95, 0) 74%);
}
#sre_hero::after {
  width: 150px;
  height: 150px;
  bottom: 8%;
  left: 5%;
  background:
    radial-gradient(circle at 50% 42%, rgba(243, 227, 188, 0.38) 0%, rgba(217, 178, 95, 0.16) 52%, rgba(217, 178, 95, 0) 74%);
}

${motionGate(`  @keyframes sreCandle {
    0%, 100% { transform: translateY(0) scale(1); opacity: 0.85; }
    50% { transform: translateY(-9px) scale(1.05); opacity: 1; }
  }
${fadeUpKeyframes("sre", 20)}

  /* Slow candle shimmer on the hero's glow discs */
  #sre_hero::before { animation: sreCandle 11s ease-in-out infinite; }
  #sre_hero::after { animation: sreCandle 14s ease-in-out infinite; animation-delay: -5s; }

  /* Poster entrance */
  #sre_hero { animation: sreFadeUp 0.9s ${EASE} both; }

${revealBlock("sre", ["numbers", "agenda", "datainput", "text", "countdown", "gallery", "faq", "map", "contact"], 35)}`)}
`;

// ── Style preset ──────────────────────────────────────────────────────

export const soireePreset: WebsiteThemeStylePreset = {
  // The hero is CSS candlelight art (no photo); heroImage just needs a valid key.
  heroImage: `${A}/shared/hero.jpg`,
  logoSrc: LOGO,
  colorPalette: { primary: GOLD, secondary: GOLD_SOFT, background: PLUM_DEEP },
  fonts: { header: DISPLAY, body: BODY_FONT },
  rounding: "rounded-lg",
  recommendedSize: "medium",
  bodyTheme: { fontColor: ON_STAGE, fontSize: "16px", layoutOption: "full-width" },
  headingsTheme: {
    h1: { fontFamily: DISPLAY, fontSize: "40px", fontColor: WHITE },
    h2: { fontFamily: DISPLAY, fontSize: "24px", fontColor: GOLD_SOFT },
  },
  buttonsTheme: {
    primary: { bgColor: GOLD, bgHoverColor: GOLD_SOFT, borderColor: GOLD, borderHoverColor: GOLD_SOFT, fontColor: PLUM_DEEP, fontHoverColor: PLUM_DEEP, fontSize: "16px", fontHoverSize: "16px", borderRadius: "rounded-lg", hoverBorderRadius: "rounded-lg" },
    secondary: { bgColor: "transparent", bgHoverColor: GOLD, borderColor: GOLD, borderHoverColor: GOLD, fontColor: GOLD, fontHoverColor: PLUM_DEEP, fontSize: "16px", fontHoverSize: "16px", borderRadius: "rounded-lg", hoverBorderRadius: "rounded-lg" },
  },
  hyperlinkTheme: { fontColor: GOLD, fontHoverColor: GOLD_SOFT, underline: false },
  formInputTheme: { labelFontColor: ON_STAGE, inputFontColor: ON_STAGE, backgroundColor: PLUM_DEEP, border: true, borderColor: LINE, borderRadius: "rounded-lg", borderStyle: "border-solid", borderWidth: "border", borderSides: [], fontFamily: BODY_FONT, fontSize: "text-sm", errorColor: "#E5484D" },
  customCss: CUSTOM_CSS,
};

// ── Template ──────────────────────────────────────────────────────────

export const soiree = (td: TranslationFn): string => {
  const B = "sre_body";

  const HERO = "sre_hero";
  const NUMBERS = "sre_numbers";
  const AGENDA = "sre_agenda";
  const DI = "sre_datainput";
  const TEXT = "sre_text";
  const COUNTDOWN = "sre_countdown";
  const GALLERY = "sre_gallery";
  const FAQ = "sre_faq";
  const MAP = "sre_map";
  const CONTACT = "sre_contact";

  return enc({
    ...scaffold(
      { ab: "sre_ab", abs: "sre_abs", body: B, ft: "sre_ft", fts: "sre_fts" },
      [HERO, NUMBERS, AGENDA, DI, TEXT, COUNTDOWN, GALLERY, FAQ, MAP, CONTACT],
      false, { background: "bg-black", padding: "p-4", borderRadius: "rounded-lg" },
    ),

    // ── Hero (midnight plum CSS poster — candle glow, centered serif) ──
    [HERO]: {
      type: { resolvedName: "Hero" }, isCanvas: false,
      props: {
        // layoutWidth is the IMAGE column share — the TEXT column gets the
        // opposite. 33% mirrors networkingNight (75% squeezed the headline
        // into a one-word-per-line column — caught on the live cover pass).
        sectionHeight: 92, contentAlignment: "center", imageLayout: "background", layoutWidth: "33%", inversed: false,
        padding: PAD, distButtons: "gap-3 wb-md:gap-4", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", buttonAlignment: "flex-row", isVisible: true,
        isVisibleHeadline: true, textHeadline: td("sreHeroEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center", headlineColor: GOLD,
        isVisibleHeader: true, textHeader: td("sreHeroHeader"), fontSizeHeader: "text-6xl", textAlignHeader: "center", headerColor: WHITE,
        isVisibleText: true, textText: td("sreHeroText"), fontSizeText: "text-lg", textAlignText: "center", textColor: ON_STAGE,
        secondaryButtonSize: "medium", secondaryButtonText: td("sreHeroSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("sreHeroPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
        // Poster surface comes from customCss (#sre_hero gradient); the image
        // is OFF so the Hero falls back to backgroundColor under the css.
        backgroundType: "image", isVisibleImage: false, imageRounded: false, backgroundOverlayColor: "transparent",
        imageSrc: `${A}/shared/hero.jpg`, backgroundColor: PLUM,
      },
      displayName: "Hero", custom: { icon: "IconSlideshow", preview: pv("Hero-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "sre_hero_eb", header: "sre_hero_hd", text: "sre_hero_tx", buttonOne: "sre_hero_b1", buttonTwo: "sre_hero_b2" },
    },
    sre_hero_eb: tc(HERO, { isVisible: true, text: td("sreHeroEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: GOLD, header: true }),
    sre_hero_hd: tc(HERO, { isVisible: true, text: td("sreHeroHeader"), fontSize: "text-6xl", textAlign: "center", displayName: "header", color: WHITE, header: true }),
    sre_hero_tx: tc(HERO, { isVisible: true, text: td("sreHeroText"), fontSize: "text-lg", textAlign: "center", displayName: "text", color: ON_STAGE, header: false }),
    sre_hero_b1: btn(HERO, { type: "secondary", linkType: "internal", linkUrl: "#" + AGENDA, text: td("sreHeroSecondaryButton"), displayName: "buttonOne" }),
    sre_hero_b2: btn(HERO, { type: "primary", linkType: "internal", linkUrl: "#" + DI, text: td("sreHeroPrimaryButton"), displayName: "buttonTwo" }),

    // ── Numbers (evening facts: courses / band / dress code / last dance) ──
    [NUMBERS]: {
      type: { resolvedName: "Numbers" }, isCanvas: true,
      props: {
        sectionHeight: 60, contentAlignment: "center", sectionMargin: SEC_MARGIN, gridColumns: "4",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", isVisible: true, layoutWidth: "100%", backgroundColor: PLUM,
        isVisibleHeadline: false, textHeadline: td("sreNumbersHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("sreNumbersHeader"), fontSizeHeader: "text-2xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("sreNumbersText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Numbers", custom: { icon: "Icon123", preview: pv("NumbersPreview"), allowedComponents: ["NumbersChild"] },
      parent: B, hidden: false, nodes: ["sre_num1", "sre_num2", "sre_num3", "sre_num4"],
      linkedNodes: { headline: "sre_num_eb", header: "sre_num_hd", text: "sre_num_tx" },
    },
    sre_num_eb: tc(NUMBERS, { isVisible: false, text: td("sreNumbersHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    sre_num_hd: tc(NUMBERS, { isVisible: true, text: td("sreNumbersHeader"), fontSize: "text-2xl", textAlign: "center", displayName: "header", color: GOLD_SOFT, header: true }),
    sre_num_tx: tc(NUMBERS, { isVisible: true, text: td("sreNumbersText"), fontSize: "text-md", textAlign: "center", displayName: "text", color: MUTE, header: false }),
    sre_num1: numbersChild(NUMBERS, 0, td),
    sre_num2: numbersChild(NUMBERS, 1, td),
    sre_num3: numbersChild(NUMBERS, 2, td),
    sre_num4: numbersChild(NUMBERS, 3, td),

    // ── Agenda (the evening, 19:00 → 00:30) ──
    [AGENDA]: {
      type: { resolvedName: "Agenda" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%", backgroundColor: SURFACE,
        isVisibleHeadline: false, textHeadline: td("sreAgendaHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("sreAgendaHeader"), fontSizeHeader: "text-2xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("sreAgendaText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Agenda", custom: { icon: "IconListCheck", preview: pv("AgendaPreview"), allowedComponents: ["AgendaChild"] },
      parent: B, hidden: false, nodes: ["sre_ag1", "sre_ag2", "sre_ag3", "sre_ag4", "sre_ag5"],
      linkedNodes: { headline: "sre_ag_eb", header: "sre_ag_hd", text: "sre_ag_tx" },
    },
    sre_ag_eb: tc(AGENDA, { isVisible: false, text: td("sreAgendaHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    sre_ag_hd: tc(AGENDA, { isVisible: true, text: td("sreAgendaHeader"), fontSize: "text-2xl", textAlign: "center", displayName: "header", color: GOLD_SOFT, header: true }),
    sre_ag_tx: tc(AGENDA, { isVisible: true, text: td("sreAgendaText"), fontSize: "text-md", textAlign: "center", displayName: "text", color: MUTE, header: false }),
    sre_ag1: agendaChild(AGENDA, 0, td),
    sre_ag2: agendaChild(AGENDA, 1, td),
    sre_ag3: agendaChild(AGENDA, 2, td),
    sre_ag4: agendaChild(AGENDA, 3, td),
    sre_ag5: agendaChild(AGENDA, 4, td),

    // ── DataInput (the RSVP card — single step, plus-one + menu) ──
    [DI]: {
      type: { resolvedName: "DataInput" }, isCanvas: false,
      props: {
        padding: PAD, sectionMargin: SEC_MARGIN, isVisible: true, textSpacing: DIST_CARD,
        backgroundColor: SURFACE, border: false, borderWidth: "border-2", borderStyle: "border-solid",
        borderSides: ["bottom"], borderRadius: undefined,
        isVisibleHeadline: true, textHeadline: td("sreDataHeadline"), fontSizeHeadline: "text-base", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("sreDataHeader"), fontSizeHeader: "text-lg", textAlignHeader: "center",
        isVisibleText: true, textText: td("sreDataText"), fontSizeText: "text-sm", textAlignText: "center",
        allowUpdate: true,
        multiStepEnabled: false, stepLabels: [], multiStepMode: "pages",
        overviewEnabled: false, stepSections: [["fields-0", "companions"]],
      },
      displayName: "DataInput", custom: { icon: "IconForms", preview: pv("DataInput-Preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "sre_di_hl", header: "sre_di_hd", text: "sre_di_tx",
        fieldsGroup0: "sre_di_f0", fieldsGroup1: "sre_di_f1", fieldsGroup2: "sre_di_f2",
        fieldsGroup3: "sre_di_f3", fieldsGroup4: "sre_di_f4",
        companionsGroup: "sre_di_comp", overviewGroup: "sre_di_ov", checkboxes: "sre_di_cb",
        button: "sre_di_btn", backButton: "sre_di_back", nextButton: "sre_di_next",
      },
    },
    sre_di_hl: tc(DI, { isVisible: true, text: td("sreDataHeadline"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "center", displayName: "headline", header: true }),
    sre_di_hd: tc(DI, { isVisible: true, text: td("sreDataHeader"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-lg", textAlign: "center", displayName: "header", header: true }),
    sre_di_tx: tc(DI, { isVisible: true, text: td("sreDataText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "center", displayName: "text", header: false }),
    sre_di_f0: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 6, groupIndex: 0, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["sre_di_acceptance", "sre_di_first", "sre_di_last", "sre_di_email", "sre_di_menu", "sre_di_note"], linkedNodes: {},
    },
    // FIRST in the group, and filter-free — the question every other
    // field in this group is gated on. See `acceptanceFi`.
    sre_di_acceptance: acceptanceFi("sre_di_f0", td("acceptanceState")),
    sre_di_first: fi("sre_di_f0", { label: td("firstName"), requiredField: true, inputFieldVariable: "firstName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    sre_di_last: fi("sre_di_f0", { label: td("lastName"), requiredField: true, inputFieldVariable: "lastName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    sre_di_email: fi("sre_di_f0", { label: td("email"), requiredField: true, inputFieldVariable: "email", gridArea: "col-span-12", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    sre_di_menu: fi("sre_di_f0", { label: td("sreFieldMenu"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "menuLine", gridArea: "col-span-12", changeable: true }),
    sre_di_note: fi("sre_di_f0", { label: td("sreFieldNote"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "note", multiline: true, gridArea: "col-span-12", changeable: true }),
    sre_di_f1: emptyFieldsGroup(DI, 1),
    sre_di_f2: emptyFieldsGroup(DI, 2),
    sre_di_f3: emptyFieldsGroup(DI, 3),
    sre_di_f4: emptyFieldsGroup(DI, 4),
    sre_di_comp: {
      type: { resolvedName: "CompanionsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 1, fieldSpacing: "medium", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER },
      displayName: "CompanionsGroup", custom: { icon: "IconUsers", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["sre_di_comp_first"], linkedNodes: {},
    },
    sre_di_comp_first: fi("sre_di_comp", { label: td("firstName"), requiredField: false, inputFieldVariable: "firstName", gridArea: "col-span-12", changeable: true, type: "companion" }),
    sre_di_ov_t: tc("sre_di_ov", { isVisible: true, text: td("overviewTitle"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "left", displayName: "overviewTitle", header: true }),
    sre_di_ov_x: tc("sre_di_ov", { isVisible: true, text: td("overviewText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "left", displayName: "overviewText", header: false }),
    sre_di_ov: {
      type: { resolvedName: "OverviewGroup" }, isCanvas: true,
      props: { isVisible: false, parentNodeId: DI, stepSections: [["fields-0", "companions"]], stepLabels: [] },
      displayName: "summaryGroup", custom: { icon: "IconListDetails" },
      parent: DI, hidden: false, nodes: ["sre_di_ov_t", "sre_di_ov_x"], linkedNodes: {},
    },
    sre_di_cb: {
      type: { resolvedName: "Checkboxes" }, isCanvas: false,
      props: { isVisible: true, checkboxes: toCheckboxItems(defaultRegistrationConsents(td)), displayName: "checkboxes" },
      displayName: "Checkboxes", custom: { icon: "IconCheckbox" },
      parent: DI, hidden: false, nodes: [], linkedNodes: {},
    },
    sre_di_btn: btn(DI, { linkType: "internal", linkUrl: "", text: td("sreDataSubmit"), submittedText: "", isSubmitted: false, enableSubmittedState: true, size: "small", widthMode: "full", buttonType: "submit", margin: BTN_FORM_SP, displayName: "button" }),
    sre_di_back: btn(DI, { linkType: "internal", type: "secondary", linkUrl: "", text: td("back"), navigationRole: "back", size: "small", widthMode: "content", isVisible: false, displayName: "backButton" }),
    sre_di_next: btn(DI, { linkType: "internal", linkUrl: "", text: td("next"), navigationRole: "next", size: "small", widthMode: "content", isVisible: false, displayName: "nextButton" }),

    // ── Text (the host's note — why this evening exists) ──
    [TEXT]: {
      type: { resolvedName: "TextSection" }, isCanvas: false,
      props: {
        buttonAlign: "center", padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%", backgroundColor: PLUM,
        isVisibleHeadline: false, textHeadline: td("sreTextHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("sreTextHeader"), fontSizeHeader: "text-2xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("sreTextBody"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("sreTextSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false, secondaryButtonAlign: "center",
        primaryButtonSize: "medium", primaryButtonText: td("sreTextPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false, primaryButtonAlign: "center",
      },
      displayName: "Text", custom: { icon: "IconAlignJustified", preview: pv("textPreview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "sre_tx_eb", header: "sre_tx_hd", text: "sre_tx_tx", buttonOne: "sre_tx_b1", buttonTwo: "sre_tx_b2" },
    },
    sre_tx_eb: tc(TEXT, { isVisible: false, text: td("sreTextHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    sre_tx_hd: tc(TEXT, { isVisible: true, text: td("sreTextHeader"), fontSize: "text-2xl", textAlign: "center", displayName: "header", color: GOLD_SOFT, header: true }),
    sre_tx_tx: tc(TEXT, { isVisible: true, text: td("sreTextBody"), fontSize: "text-md", textAlign: "center", displayName: "text", color: ON_STAGE, header: false }),
    sre_tx_b1: btn(TEXT, { type: "secondary", linkType: "internal", linkUrl: "#" + CONTACT, text: td("sreTextSecondaryButton"), displayName: "buttonOne" }),
    sre_tx_b2: btn(TEXT, { type: "primary", linkType: "internal", linkUrl: "#" + DI, text: td("sreTextPrimaryButton"), displayName: "buttonTwo" }),

    // ── Countdown ──
    [COUNTDOWN]: {
      type: { resolvedName: "Countdown" }, isCanvas: false,
      props: {
        sectionHeight: 40, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distButtonCountdown: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true, buttonAlign: "left", backgroundColor: SURFACE,
        isVisibleHeadline: true, textHeadline: td("sreCountdownEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("sreCountdownHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("sreCountdownText"), fontSizeText: "text-base", textAlignText: "left",
        buttonSize: "medium", buttonText: td("sreCountdownButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
        isVisiblCountdown: true, countdownSizeDate: "text-5xl", countdownFontTypeDate: DISPLAY, countdownFontColorDate: GOLD_SOFT,
        countdownSize: "text-base", countdownFontColor: GOLD, countdownBorderColor: LINE,
      },
      displayName: "Countdown", custom: { icon: "IconAlarm", preview: pv("Countdown-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "sre_cd_eb", header: "sre_cd_hd", text: "sre_cd_tx", button: "sre_cd_btn" },
    },
    sre_cd_eb: tc(COUNTDOWN, { isVisible: true, text: td("sreCountdownEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: GOLD, header: true }),
    sre_cd_hd: tc(COUNTDOWN, { isVisible: true, text: td("sreCountdownHeader"), fontSize: "text-4xl", textAlign: "left", displayName: "header", color: WHITE, header: true }),
    sre_cd_tx: tc(COUNTDOWN, { isVisible: true, text: td("sreCountdownText"), fontSize: "text-base", textAlign: "left", displayName: "text", color: MUTE, header: false }),
    sre_cd_btn: btn(COUNTDOWN, { linkType: "internal", linkUrl: "#" + DI, text: td("sreCountdownButton"), displayName: "ButtonChild" }),

    // ── Gallery (last year's evening) — hidden by default (no photo library) ──
    [GALLERY]: {
      type: { resolvedName: "Gallery" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", distButtonGallery: "gap-3 wb-md:gap-4", distEntries: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, buttonAlign: "center", gridColumns: "3", layoutWidth: "100%", backgroundColor: PLUM,
        isVisibleHeadline: false, textHeadline: td("sreGalleryHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("sreGalleryHeader"), fontSizeHeader: "text-2xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("sreGalleryText"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("sreHeroSecondaryButton"), isVisibleSecondaryButton: false,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("sreHeroPrimaryButton"), isVisiblePrimaryButton: false,
        primaryButtonBorder: true, primaryButtonUnderline: false,
      },
      displayName: "Gallery", custom: { icon: "IconPhoto", preview: pv("Gallery-preview"), allowedComponents: ["ImageChild"] },
      parent: B, hidden: false,
      nodes: ["sre_g1", "sre_g2", "sre_g3", "sre_g4", "sre_g5", "sre_g6"],
      linkedNodes: { headline: "sre_g_eb", header: "sre_g_hd", text: "sre_g_tx", secondary: "sre_g_b2", primary: "sre_g_b1" },
    },
    sre_g_eb: tc(GALLERY, { isVisible: false, text: td("sreGalleryHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    sre_g_hd: tc(GALLERY, { isVisible: true, text: td("sreGalleryHeader"), fontSize: "text-2xl", textAlign: "center", displayName: "header", color: GOLD_SOFT, header: true }),
    sre_g_tx: tc(GALLERY, { isVisible: true, text: td("sreGalleryText"), fontSize: "text-md", textAlign: "center", displayName: "text", color: MUTE, header: false }),
    sre_g_b2: btn(GALLERY, { type: "secondary", text: td("sreHeroSecondaryButton"), isVisible: false, displayName: "ButtonChild" }),
    sre_g_b1: btn(GALLERY, { text: td("sreHeroPrimaryButton"), isVisible: false, displayName: "ButtonChild" }),
    sre_g1: galleryImage(GALLERY, 0),
    sre_g2: galleryImage(GALLERY, 1),
    sre_g3: galleryImage(GALLERY, 2),
    sre_g4: galleryImage(GALLERY, 3),
    sre_g5: galleryImage(GALLERY, 4),
    sre_g6: galleryImage(GALLERY, 5),

    // ── FaQ ──
    [FAQ]: {
      type: { resolvedName: "FaQ" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%", backgroundColor: SURFACE,
        isVisibleHeadline: false, textHeadline: td("sreFaqHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("sreFaqHeader"), fontSizeHeader: "text-2xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("sreFaqText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "FaQ", custom: { icon: "IconQuestionMark", preview: pv("faqPreview"), allowedComponents: ["FaQChild"] },
      parent: B, hidden: false, nodes: ["sre_fq1", "sre_fq2", "sre_fq3", "sre_fq4", "sre_fq5"],
      linkedNodes: { headline: "sre_fq_eb", header: "sre_fq_hd", text: "sre_fq_tx" },
    },
    sre_fq_eb: tc(FAQ, { isVisible: false, text: td("sreFaqHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    sre_fq_hd: tc(FAQ, { isVisible: true, text: td("sreFaqHeader"), fontSize: "text-2xl", textAlign: "center", displayName: "header", color: GOLD_SOFT, header: true }),
    sre_fq_tx: tc(FAQ, { isVisible: true, text: td("sreFaqText"), fontSize: "text-md", textAlign: "center", displayName: "text", color: MUTE, header: false }),
    sre_fq1: faqChild(FAQ, 0, td),
    sre_fq2: faqChild(FAQ, 1, td),
    sre_fq3: faqChild(FAQ, 2, td),
    sre_fq4: faqChild(FAQ, 3, td),
    sre_fq5: faqChild(FAQ, 4, td),

    // ── Map (the house) — present but hidden by default ──
    [MAP]: {
      type: { resolvedName: "Map" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentMap: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false, backgroundColor: PLUM,
        isVisibleHeadline: false, textHeadline: td("sreMapHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("sreMapHeader"), fontSizeHeader: "text-2xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("sreMapText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("sreMapButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "left",
        mapRounded: "rounded-lg",
        mapZoom: 14, mapType: "m", aspectRatio: "aspect-[4/3]", isVisibleMap: true, mapLocation: td("sreMapLocation"),
      },
      displayName: "Map", custom: { icon: "IconMap2", preview: pv("Map-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "sre_mp_eb", header: "sre_mp_hd", text: "sre_mp_tx", button: "sre_mp_btn" },
    },
    sre_mp_eb: tc(MAP, { isVisible: false, text: td("sreMapHeader"), fontSize: "text-lg", textAlign: "left", displayName: "headline", header: true }),
    sre_mp_hd: tc(MAP, { isVisible: true, text: td("sreMapHeader"), fontSize: "text-2xl", textAlign: "left", displayName: "header", color: GOLD_SOFT, header: true }),
    sre_mp_tx: tc(MAP, { isVisible: true, text: td("sreMapText"), fontSize: "text-md", textAlign: "left", displayName: "text", color: ON_STAGE, header: false }),
    sre_mp_btn: btn(MAP, { text: td("sreMapButton"), displayName: "button" }),

    // ── Contact (the hosts) ──
    [CONTACT]: {
      type: { resolvedName: "Contact" }, isCanvas: true,
      props: {
        sectionHeight: 70, contentAlignment: "center", infoSection: true, backgroundColor: PLUM,
        padding: PAD, sectionMargin: SEC_MARGIN, textSpacing: "space-y-3 wb-md:space-y-5 wb-lg:space-y-6", isVisible: true,
        infoWidth: "50%", inversed: false,
        isVisibleHeadline: true, textHeadline: td("sreContactEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("sreContactHeader"), fontSizeHeader: "text-2xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("sreContactText"), fontSizeText: "text-md", textAlignText: "left",
        isSocialVisible: true, socialIcons: [{ platform: "instagram", url: "https://instagram.com" }, { platform: "linkedin", url: "https://linkedin.com" }], socialSize: 24, socialPosition: "left",
        buttonSize: "medium", buttonText: td("sreContactButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
      },
      displayName: "Contact", custom: { icon: "IconForms", preview: pv("Contact-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "sre_ct_eb", header: "sre_ct_hd", text: "sre_ct_tx",
        firstName: "sre_ct_first", lastName: "sre_ct_last", eMail: "sre_ct_email",
        phoneNumber: "sre_ct_phone", Message: "sre_ct_msg", button: "sre_ct_btn",
      },
    },
    sre_ct_eb: tc(CONTACT, { isVisible: true, text: td("sreContactEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: GOLD, header: true }),
    sre_ct_hd: tc(CONTACT, { isVisible: true, text: td("sreContactHeader"), fontSize: "text-2xl", textAlign: "left", displayName: "header", color: WHITE, header: true }),
    sre_ct_tx: tc(CONTACT, { isVisible: true, text: td("sreContactText"), fontSize: "text-md", textAlign: "left", displayName: "text", color: ON_STAGE, header: false }),
    sre_ct_first: fi(CONTACT, { label: td("firstName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "firstName", changeable: false }),
    sre_ct_last: fi(CONTACT, { label: td("lastName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "lastName", changeable: false }),
    sre_ct_email: fi(CONTACT, { label: td("email"), overwriteType: "EMAIL", requiredField: true, inputFieldVariable: "email", changeable: false, locked: true }),
    sre_ct_phone: fi(CONTACT, { label: td("phoneNumber"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "phone", changeable: false }),
    sre_ct_msg: fi(CONTACT, { label: td("messageLabel"), overwriteType: "STRING", requiredField: true, inputFieldVariable: "message", multiline: true, gridArea: "col-span-12", changeable: false, locked: true }),
    sre_ct_btn: btn(CONTACT, { linkType: "internal", linkUrl: "", text: td("sreContactButton"), widthMode: "full", buttonType: "submit", displayName: "button", fixedNavigation: false }),
  });
};

// ── Meta sections (AppBar + Footer) ───────────────────────────────────

/** Plum bar, gold hairline, logo + anchor nav + gold "Reserve" CTA. */
export const soireeAppbar = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "AppBarSection" }, isCanvas: false,
    props: {
      padding: "p-4 wb-md:p-5 wb-lg:p-6", isVisible: true,
      backgroundColor: PLUM,
      border: true, borderColor: GOLD, borderWidth: "border", borderStyle: "border-solid", borderSides: ["bottom"],
      sectionOrder: ["logo", "links", "social"],
      isLogoVisible: true, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-10", logoPosition: "left",
      isLinksVisible: true, linksPosition: "center",
      links: [
        { label: td("sreLinkProgram"), url: "#sre_agenda", linkType: "internal" },
        { label: td("sreLinkFaq"), url: "#sre_faq", linkType: "internal" },
        { label: td("sreLinkContact"), url: "#sre_contact", linkType: "internal" },
      ],
      linksColor: GOLD_SOFT, linksFontSize: "text-sm", linksFontWeight: "font-medium",
      isSocialVisible: false, socialIcons: [], socialPosition: "right", socialSize: 24,
      logoutButtonSize: "small", logoutButtonText: td("sreHeroPrimaryButton"),
      logoutButtonBorder: true, logoutButtonBackgroundColor: GOLD, logoutButtonColor: PLUM_DEEP, logoutButtonBorderColor: GOLD,
    },
    displayName: "AppBar", custom: { icon: "IconLayoutNavbar" },
    parent: "DCH9WIIT55", hidden: false, nodes: [], linkedNodes: {},
  });

/** Deep-plum footer, gold top rule, three link groups + social + copyright. */
export const soireeFooter = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "FooterSection" }, isCanvas: false,
    props: {
      padding: "px-4 py-10 wb-md:px-6 wb-lg:px-8", isVisible: true,
      backgroundColor: PLUM_DEEP,
      border: true, borderColor: GOLD, borderWidth: "border-4", borderStyle: "border-solid", borderSides: ["top"],
      sectionOrder: ["links", "social"],
      isLogoVisible: false, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-auto",
      isLinkGroupsVisible: true,
      linkGroups: [
        { title: td("sreFooterEvening"), links: [
          { label: td("sreLinkProgram"), url: "#sre_agenda", linkType: "internal" },
          { label: td("sreLinkFaq"), url: "#sre_faq", linkType: "internal" },
          { label: td("sreLinkRsvp"), url: "/register", linkType: "internal" },
        ] },
        // Event practicalities — Maps + add-to-calendar are EXTERNAL (open in
        // a new tab). URLs are placeholders the organiser edits per event.
        { title: td("sreFooterEvent"), links: [
          { label: td("sreLinkLocation"), url: "https://www.google.com/maps/search/?api=1&query=Hamburg", linkType: "external" },
          { label: td("sreLinkCalendar"), url: "https://calendar.google.com/calendar/render?action=TEMPLATE&text=Soiree", linkType: "external" },
          { label: td("sreLinkContact"), url: "#sre_contact", linkType: "internal" },
        ] },
        { title: td("sreFooterLegal"), links: [
          { label: td("sreLinkPrivacy"), url: "/privacy", linkType: "internal" },
          { label: td("sreLinkImprint"), url: "/imprint", linkType: "internal" },
          { label: td("sreLinkTerms"), url: "/terms", linkType: "internal" },
        ] },
      ],
      linksTitleColor: GOLD_SOFT, linksTitleFontSize: "text-sm", linksTitleFontWeight: "font-semibold",
      linksColor: MUTE, linksFontSize: "text-sm", linksFontWeight: "font-normal", linksHoverColor: "hover:font-primary",
      isSocialVisible: true,
      socialIcons: [
        { altText: "instagram", platform: "instagram", url: "https://instagram.com" },
        { altText: "linkedin", platform: "linkedin", url: "https://linkedin.com" },
      ],
      socialColor: MUTE, socialHoverColor: "hover:font-primary", socialSize: 20, socialTitle: td("followUs"),
      isCopyrightVisible: true, copyrightColor: "#7E7290", copyrightFontSize: "text-xs", copyrightCompanyName: td("yourCompany"),
    },
    displayName: "Footer", custom: { icon: "IconMenu2" },
    parent: "xaUW2UDZT8", hidden: false, nodes: [], linkedNodes: {},
  });
